import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useCreateSlackLinkCode } from '../../api/slack.ts';
import { Button } from '../../components/ui.tsx';
import { CopyField } from './CopyField.tsx';
import { formatDuration } from '../../lib/format.ts';
import { ApiError } from '../../api/client.ts';

const SLACK_DOCS_URL = 'mailto:we@seenlabs.com?subject=SeenCast%20Slack%20setup';

/** Abstract hub mark — deliberately not Slack's trademarked hashtag glyph. */
function SlackGlyph() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Settings → Connect Slack (docs/05 §Slack). Mints a one-time link code; the actual
 *  linking happens in Slack (DM `link <code>` or `/seencast link <code>`), not here. */
export function SlackSection() {
  const createCode = useCreateSlackLinkCode();
  const [now, setNow] = useState(() => Date.now());

  // Tick once a second while a code is showing so the countdown stays live.
  useEffect(() => {
    if (!createCode.data) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [createCode.data]);

  const remainingMs = createCode.data ? createCode.data.expires_at - now : 0;
  const expired = createCode.data != null && remainingMs <= 0;

  function generate() {
    createCode.mutate(undefined, {
      onSuccess: () => setNow(Date.now()),
      onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to generate a code'),
    });
  }

  return (
    <div className="card card-pad">
      <div className="card-title row" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
        <span className="slack-glyph">
          <SlackGlyph />
        </span>
        Slack
      </div>
      <p className="text-dim mb-4">
        Drive SeenCast from Slack — DM the bot in your workspace to check on screens, stage
        changes, and approve them without opening the CMS.
      </p>

      {!createCode.data ? (
        <Button variant="primary" onClick={generate} disabled={createCode.isPending}>
          Generate link code
        </Button>
      ) : (
        <div className={`slack-result${expired ? ' expired' : ''}`}>
          <div className="slack-code-display">{createCode.data.code}</div>
          <div className="slack-countdown mt-2" role="status">
            {expired ? 'Code expired' : `Expires in ${formatDuration(remainingMs)}`}
          </div>
          <div className="mt-2">
            <CopyField value={createCode.data.code} label="Slack link code" />
          </div>

          <ol className="slack-steps">
            <li>Open the SeenCast app in Slack.</li>
            <li>
              DM the bot <code className="mono">link {createCode.data.code}</code> or run{' '}
              <code className="mono">/seencast link {createCode.data.code}</code>.
            </li>
            <li>Done — Slack is linked to your account.</li>
          </ol>
        </div>
      )}

      {createCode.data && (
        <Button size="sm" variant="ghost" className="mt-4" onClick={generate} disabled={createCode.isPending}>
          {expired ? 'Regenerate code' : 'Generate a new code'}
        </Button>
      )}

      <div className="hint-box info mt-4">
        Ask your admin to install the SeenCast Slack app first if linking doesn&apos;t work yet.{' '}
        <a href={SLACK_DOCS_URL} target="_blank" rel="noreferrer">
          Setup guide
        </a>
        .
      </div>
    </div>
  );
}
